/* eslint-disable max-len */
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Fab,
  FormControl,
  Grid,
  ImageList,
  ImageListItem,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useNavigate } from "react-router-dom";
import classes from "./style";
import { RootState } from "store";
import { useSelector, useDispatch } from "react-redux";
import Genders from "models/Enums/Genders";
import profilService from "services/profilService";
import User from "models/User";
import { SET_USER } from "slices/global.slice";
import { useDebouncedCallback } from "use-debounce";
import { getImageUrl, readFileDataAsBase64 } from "utils/imageUtils";
import ImagesCarousel from "components/carousel/Carousel";

const Passeport: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const { user, transports, countries, stayTypes } = useSelector(
    (state: RootState) => state.globalSlice
  );

  useEffect(() => {
    if (!user) navigate("/");
  }, [user, navigate]);
  if (!user) return null;

  const [imgUrl, setImgUrl] = useState<string | undefined>(user.profilePicture);
  const [personalImages, setPersonalImages] = useState<string[]>(user.personalPictures ?? []);
  const [userDestinations, setUserDestinations] = useState<string[]>(user.pastTravels ?? []);
  const [userTransports, setUserTransports] = useState<string[]>(user.favoriteTransports ?? []);
  const [userStayTypes, setUserStayTypes] = useState<string[]>(user.favoriteStayTypes ?? []);
  const [userDescription, setUserDescription] = useState<string>(user.description ?? "");

  // valeurs contrôlées des 3 dropdowns "Ajouter ..."
  const [toAddDestination, setToAddDestination] = useState<string>("");
  const [toAddTransport, setToAddTransport] = useState<string>("");
  const [toAddStayType, setToAddStayType] = useState<string>("");

  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const personalInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedImage, setSelectedImage] = useState<{ img: string; title?: string } | null>(null);

  const updateUser = (u: User) => dispatch(SET_USER(u));

  const debounceDescription = useDebouncedCallback((desc: string) => {
    profilService.addDescription(desc);
    updateUser({ ...user, description: desc });
  }, 500);

  const genderLabel = useMemo(() => {
    const g = Genders[user.gender];
    if (g === "Male") return "Homme";
    if (g === "Female") return "Femme";
    return "Autre";
  }, [user.gender]);

  const calculateAge = (birthday: Date | string | undefined): number | null => {
    if (!birthday) return null;
    const b = new Date(birthday);
    if (Number.isNaN(b.getTime())) return null;
    return Math.floor((Date.now() - b.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  };

  const onAvatarClick = () => avatarInputRef.current?.click();
  const onPersonalClick = () => personalInputRef.current?.click();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = (await readFileDataAsBase64(file)) as string;
    const r = await profilService.updateProfilPicture(base64);
    setImgUrl(r);
    updateUser({ ...user, profilePicture: r });
  };

  const handlePersonalChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = (await readFileDataAsBase64(file)) as string;
    const r = await profilService.addPersonalPicture(base64);
    const next = [...personalImages, r];
    setPersonalImages(next);
    updateUser({ ...user, personalPictures: next });
  };

  // ========= Chips helpers (ajout/suppression) =========
  const addDestination = (name: string) => {
    if (!name || userDestinations.includes(name)) return;
    profilService.addDestination(name);
    const next = [...userDestinations, name];
    setUserDestinations(next);
    updateUser({ ...user, pastTravels: next });
  };
  const removeDestination = (name: string) => {
    profilService.removeDestination(name);
    const next = userDestinations.filter((d) => d !== name);
    setUserDestinations(next);
    updateUser({ ...user, pastTravels: next });
  };

  const addTransport = (name: string) => {
    if (!name || userTransports.includes(name)) return;
    profilService.addTransport(name);
    const next = [...userTransports, name];
    setUserTransports(next);
    updateUser({ ...user, favoriteTransports: next });
  };
  const removeTransport = (name: string) => {
    profilService.removeTransport(name);
    const next = userTransports.filter((t) => t !== name);
    setUserTransports(next);
    updateUser({ ...user, favoriteTransports: next });
  };

  const addStayType = (name: string) => {
    if (!name || userStayTypes.includes(name)) return;
    profilService.addStayType(name);
    const next = [...userStayTypes, name];
    setUserStayTypes(next);
    updateUser({ ...user, favoriteStayTypes: next });
  };
  const removeStayType = (name: string) => {
    profilService.removeStayType(name);
    const next = userStayTypes.filter((s) => s !== name);
    setUserStayTypes(next);
    updateUser({ ...user, favoriteStayTypes: next });
  };

  // ========= Options possibles – on masque celles déjà sélectionnées =========
  const destinationOptions = useMemo(
    () => countries.map((c) => c.name).filter((n) => !userDestinations.includes(n)),
    [countries, userDestinations]
  );
  const transportOptions = useMemo(
    () => transports.map((t) => t.name).filter((n) => !userTransports.includes(n)),
    [transports, userTransports]
  );
  const stayTypeOptions = useMemo(
    () => stayTypes.map((s) => s.name).filter((n) => !userStayTypes.includes(n)),
    [stayTypes, userStayTypes]
  );

  // ========= Handlers dropdown "Ajouter ..." =========
  const handleAddDestination = (e: SelectChangeEvent<string>) => {
    const v = e.target.value as string;
    setToAddDestination("");
    if (v) addDestination(v);
  };
  const handleAddTransport = (e: SelectChangeEvent<string>) => {
    const v = e.target.value as string;
    setToAddTransport("");
    if (v) addTransport(v);
  };
  const handleAddStayType = (e: SelectChangeEvent<string>) => {
    const v = e.target.value as string;
    setToAddStayType("");
    if (v) addStayType(v);
  };

  const save = async () => {
    // Tu persistes déjà à chaque action via les services,
    // on synchronise simplement le store et on reste sur la page
    dispatch(SET_USER({ ...user }));
    navigate("/passeport");
  };

  const age = calculateAge(user.birthDay);
  const safeAvatarUrl = getImageUrl(imgUrl);

  return (
    <>
      <Container disableGutters sx={classes.emptyPasseport}>
        <Typography variant="h1" my={5}>
          PASSEPORT
        </Typography>

        <Grid container sx={classes.messagerie} my={3} spacing={isLgUp ? 2 : 0}>
          {/* Avatar */}
          <Grid item xs={12} md={6} lg={3} sx={{ ...classes.profileBloc, pb: !isLgUp ? 3 : 0 }}>
            <Box sx={{ position: "relative", width: "fit-content" }} onClick={onAvatarClick}>
              <Avatar
                sx={classes.profileAvatar}
                alt={`${user.firstName} ${user.lastName}`}
                src={safeAvatarUrl}
              />
              <Box sx={classes.uploadAvatar}>
                <AddAPhotoIcon />
              </Box>
            </Box>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleAvatarChange}
            />
          </Grid>

          {/* Infos & chips */}
          <Grid item xs={12} md={6} lg={5}>
            <Card sx={{ p: 2 }}>
              <Box display="flex" flexDirection="column">
                <Typography component="div" variant="h5">
                  {`${user.firstName} ${user.lastName}`}
                </Typography>
                <Typography component="div" variant="body2">
                  {`${genderLabel}${age !== null ? `, ${age} ans` : ""}`}
                </Typography>
              </Box>

              {/* Destinations */}
              <Box mt={3}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Vos précédentes destinations
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  {userDestinations.map((name) => (
                    <Chip
                      label={name}
                      color='primary'
                      onDelete={() => removeDestination(name)}
                      variant="outlined"
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        fontWeight: 400,
                        fontSize: 16,
                        "& .MuiChip-deleteIcon": {
                          color: "white",
                          opacity: 0.8,
                        },
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      }}
                    />
                  ))}
                </Box>

                <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel id="dest-add-label">Ajouter une destination</InputLabel>
                  <Select
                    labelId="dest-add-label"
                    label="Ajouter une destination"
                    value={toAddDestination}
                    onChange={handleAddDestination}
                    renderValue={(v) => (v ? v : "Ajouter une destination")}
                  >
                    {destinationOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        Aucune destination disponible
                      </MenuItem>
                    )}
                    {destinationOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box mt={3}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Transports préférés
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  {userTransports.map((name) => (
                    <Chip
                      key={`tr-${name}`}
                      label={name}
                      color='primary'
                      onDelete={() => removeTransport(name)}
                      variant="outlined"
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        fontWeight: 400,
                        fontSize: 16,
                        "& .MuiChip-deleteIcon": {
                          color: "white",
                          opacity: 0.8,
                        },
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      }}
                    />
                  ))}
                </Box>

                <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel id="tr-add-label">Ajouter un transport</InputLabel>
                  <Select
                    labelId="tr-add-label"
                    label="Ajouter un transport"
                    value={toAddTransport}
                    onChange={handleAddTransport}
                    renderValue={(v) => (v ? v : "Ajouter un transport")}
                  >
                    {transportOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        Aucun transport disponible
                      </MenuItem>
                    )}
                    {transportOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box mt={3}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Votre personnalité de voyageurs
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  {userStayTypes.map((name) => (
                    <Chip
                      key={`st-${name}`}
                      label={name}
                      onDelete={() => removeStayType(name)}
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        fontWeight: 400,
                        fontSize: 16,
                        "& .MuiChip-deleteIcon": {
                          color: "white",
                          opacity: 0.8,
                        },
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      }}
                    />

                  ))}
                </Box>

                <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
                  <InputLabel id="st-add-label">Ajouter une ambiance</InputLabel>
                  <Select
                    labelId="st-add-label"
                    label="Ajouter une ambiance"
                    value={toAddStayType}
                    onChange={handleAddStayType}
                    renderValue={(v) => (v ? v : "Ajouter une ambiance")}
                  >
                    {stayTypeOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        Aucune ambiance disponible
                      </MenuItem>
                    )}
                    {stayTypeOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Description"
                value={userDescription}
                variant="outlined"
                multiline
                fullWidth
                sx={{ mt: 3 }}
                onChange={(e) => {
                  setUserDescription(e.target.value);
                  debounceDescription(e.target.value);
                }}
              />

              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button variant="contained" color="primary" onClick={save}>
                  Enregistrer
                </Button>
              </Box>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            lg={4}
            sx={isLgUp ? classes.imageListBloc : undefined}
            style={{ position: "relative" }}
          >
            <Typography component="div" variant="h5" mb={2}>
              Ajouter vos photos
            </Typography>

            <Box onClick={onPersonalClick} sx={{ width: "100%", height: 500, cursor: "pointer" }}>
              <ImageList variant="quilted" cols={2} gap={8}>
                {[0, 1, 2].map((_, i) => {
                  const hasImage = personalImages.length > i;
                  const src = hasImage ? getImageUrl(personalImages[i]) : undefined;
                  return (
                    <ImageListItem key={`pi-${i}`} cols={i === 0 ? 2 : 1} rows={1}>
                      {hasImage ? (
                        <img
                          src={src}
                          alt={`photo-${i}`}
                          width="100%"
                          height="220px"
                          loading="lazy"
                          style={{ objectFit: "cover", borderRadius: 8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage({ img: src!, title: personalImages[i] });
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 220,
                            borderRadius: 2,
                            border: "1px dashed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                          }}
                        >
                          <AddOutlinedIcon sx={{ mr: 1 }} />
                          Ajouter une photo
                        </Box>
                      )}
                    </ImageListItem>
                  );
                })}
              </ImageList>
            </Box>

            <input
              ref={personalInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handlePersonalChange}
            />

            {personalImages.length > 3 && (
              <Fab
                color="primary"
                size="small"
                sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                onClick={() =>
                  setSelectedImage({ img: getImageUrl(personalImages[0]), title: personalImages[0] })
                }
              >
                <Typography color="white" fontWeight="bold">
                  +{personalImages.length - 3}
                </Typography>
              </Fab>
            )}
          </Grid>
        </Grid>
      </Container>

      {personalImages?.length > 0 && (
        <ImagesCarousel
          open={!!selectedImage}
          images={personalImages.map((i) => ({ img: getImageUrl(i), title: i }))}
          selectedImage={selectedImage}
          handleClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
};

export default Passeport;
